export const termsContent = `
  <h1>PrezziePal Terms of Service</h1>
  <p><strong>Effective Date: March 12, 2025</strong></p>
  
  <h2>Introduction</h2>
  <p>These Terms of Service ("Terms") govern your access to and use of the PrezziePal mobile application and related services (collectively, the "Service") provided by PrezziePal ("we," "our," or "us").</p>
  <p>Please read these Terms carefully before using our Service. By accessing or using the Service, you agree to be bound by these Terms and our Privacy Policy. If you do not agree to these Terms, you may not access or use the Service.</p>
  
  <h2>Eligibility</h2>
  <p>You must be at least 13 years old to use the Service. By using the Service, you represent and warrant that you meet this eligibility requirement and that you have the right, authority, and capacity to enter into these Terms.</p>
  
  <h2>Account Registration</h2>
  <h3>Creating an Account</h3>
  <p>To use certain features of the Service, you must create an account. When registering for an account, you agree to provide accurate, current, and complete information, and to update this information to keep it accurate, current, and complete.</p>
  
  <h3>Account Security</h3>
  <p>You are responsible for safeguarding your password and for all activities that occur under your account. You agree to notify us immediately if you suspect any unauthorized access to or use of your account.</p>
  
  <h3>Contact Synchronization</h3>
  <p>Our Service allows you to import contacts from your device. By using this feature, you:</p>
  <ul>
    <li>Represent that you have the necessary permissions to share these contacts with us</li>
    <li>Agree to allow us to use this contact information as described in our Privacy Policy</li>
    <li>Consent to our use of this contact data for business purposes, including but not limited to marketing, business development, and partnership opportunities</li>
    <li>Understand that we may retain this contact information even if you later delete it from the Service</li>
  </ul>
  
  <h2>User Content</h2>
  <h3>Content You Provide</h3>
  <p>The Service allows you to upload, post, send, and store content, including but not limited to text, photos, videos, links, and other materials ("User Content"). You retain all rights in your User Content, but you grant us a non-exclusive, transferable, sublicensable, royalty-free, worldwide license to use, host, store, reproduce, modify, create derivative works, communicate, publish, publicly display, and distribute your User Content in connection with operating and providing the Service.</p>
  
  <h3>Content Guidelines</h3>
  <p>You agree not to post User Content that:</p>
  <ul>
    <li>Violates any applicable law or regulation</li>
    <li>Infringes on the intellectual property rights of others</li>
    <li>Contains viruses or other malicious code</li>
    <li>Is defamatory, obscene, pornographic, or invasive of another's privacy</li>
    <li>Constitutes unsolicited or unauthorized advertising or promotional materials</li>
    <li>Impersonates any person or entity</li>
    <li>Contains personal or sensitive information about others without their consent</li>
  </ul>
  
  <h3>Monitoring and Enforcement</h3>
  <p>We do not undertake to review all User Content, but we reserve the right to remove or refuse any User Content for any or no reason.</p>
  
  <h2>Wishlist Management</h2>
  <h3>Wishlist Creation and Sharing</h3>
  <p>The Service allows you to create and manage wishlists for birthdays and other occasions. By creating a wishlist, you understand that:</p>
  <ul>
    <li>Your wishlist information may be visible to your contacts on the Service</li>
    <li>We may use your wishlist data to provide personalized recommendations</li>
    <li>Product information and preferences may be used for service improvement</li>
  </ul>
  
  <h3>Product Links</h3>
  <p>Our Service allows you to add product links to your wishlist. These links may contain affiliate codes, and we may earn a commission when purchases are made through these links. By using the wishlist feature, you consent to our use of affiliate links.</p>
  
  <h3>Pricing Information</h3>
  <p>We strive to provide accurate pricing information for wishlist items, but we do not guarantee the accuracy of prices displayed. The final price of any item is determined by the retailer at the time of purchase.</p>
  
  <h2>Intellectual Property</h2>
  <h3>Our Content</h3>
  <p>The Service and its original content, features, and functionality (excluding User Content) are owned by Instauratio and are protected by copyright, trademark, and other intellectual property laws. You may not use, copy, reproduce, distribute, or create derivative works of such content without our prior written consent.</p>
  
  <h3>Trademarks</h3>
  <p>The PrezziePal name and logo are trademarks of Instauratio. You may not use these marks without our prior written consent.</p>
  
  <h2>Prohibited Activities</h2>
  <p>You agree not to:</p>
  <ul>
    <li>Use the Service for any illegal purpose</li>
    <li>Attempt to gain unauthorized access to the Service or any related systems</li>
    <li>Interfere with or disrupt the Service or servers connected to the Service</li>
    <li>Introduce any viruses, trojans, worms, or other harmful material</li>
    <li>Use automated methods to access or use the Service</li>
    <li>Reverse engineer, decompile, or disassemble any portion of the Service</li>
    <li>Use the Service to collect or harvest personal information about other users</li>
    <li>Use the Service to send unsolicited communications</li>
  </ul>
  
  <h2>Data Collection and Analytics</h2>
  <h3>Usage Data</h3>
  <p>By using our Service, you acknowledge and agree that:</p>
  <ul>
    <li>We collect data about how you use the Service, including but not limited to features accessed, time spent, interactions, and preferences</li>
    <li>We may analyze this data to improve our Service, develop new features, and understand user behavior</li>
    <li>We may sell or share anonymized or aggregated usage data with third parties for commercial purposes</li>
    <li>We may combine your usage data with information from other sources to enhance our analytics capabilities</li>
  </ul>
  
  <h3>Third-Party Analytics</h3>
  <p>We use third-party analytics services to help us understand how users engage with our Service. These third parties may use cookies, web beacons, and other tracking technologies to collect information about your use of our Service. By using our Service, you consent to the processing of data about you by these analytics providers in accordance with their respective privacy policies.</p>
  
  <h3>Third-Party Service Providers</h3>
  <p>We may use a variety of third-party service providers to help us deliver, maintain, and improve our Service, including but not limited to providers for:</p>
  <ul>
    <li>Hosting and cloud services</li>
    <li>Authentication and identity verification</li>
    <li>Analytics and data processing</li>
    <li>Push notifications and messaging</li>
    <li>Image and media storage</li>
    <li>Deep linking and attribution</li>
  </ul>
  <p>We may change our service providers at any time in our sole discretion. By using our Service, you acknowledge that your information may be processed by these third parties in accordance with their respective terms and privacy policies.</p>
  
  <h2>Disclaimers</h2>
  <h3>Service Provided "As Is"</h3>
  <p>THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING, BUT NOT LIMITED TO, IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND NON-INFRINGEMENT.</p>
  
  <h3>No Guarantee of Availability</h3>
  <p>We do not guarantee that the Service will be available at all times or that it will be error-free, uninterrupted, or secure.</p>
  
  <h3>Gift Recommendations</h3>
  <p>Any gift recommendations provided through the Service are suggestions only and do not constitute an endorsement of any specific product or retailer.</p>
  
  <h2>Limitation of Liability</h2>
  <p>TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL PREZZIEPAL BE LIABLE FOR ANY INDIRECT, PUNITIVE, INCIDENTAL, SPECIAL, CONSEQUENTIAL DAMAGES, OR ANY DAMAGES WHATSOEVER INCLUDING, WITHOUT LIMITATION, DAMAGES FOR LOSS OF USE, DATA, OR PROFITS, ARISING OUT OF OR IN ANY WAY CONNECTED WITH THE USE OR PERFORMANCE OF THE SERVICE.</p>
  
  <h2>Indemnification</h2>
  <p>You agree to defend, indemnify, and hold harmless PrezziePal and its officers, directors, employees, and agents from and against any claims, liabilities, damages, losses, and expenses, including, without limitation, reasonable legal and accounting fees, arising out of or in any way connected with your access to or use of the Service, your violation of these Terms, or your violation of any rights of another.</p>
  
  <h2>Modifications to the Service</h2>
  <p>We reserve the right to modify or discontinue, temporarily or permanently, the Service or any features or portions thereof without prior notice. You agree that we will not be liable for any modification, suspension, or discontinuance of the Service or any part thereof.</p>
  
  <h2>Changes to These Terms</h2>
  <p>We may update these Terms from time to time. We will notify you of any changes by posting the new Terms on this page and updating the "Effective Date" at the top of this page. You are advised to review these Terms periodically for any changes. Continued use of the Service after any such changes shall constitute your consent to such changes.</p>
  
  <h2>Governing Law</h2>
  <p>These Terms shall be governed by and construed in accordance with the laws of England and Wales, without regard to its conflict of law provisions.</p>
  
  <h2>Dispute Resolution</h2>
  <p>Any dispute arising from or relating to the subject matter of these Terms shall be finally settled in accordance with the laws of England and Wales. Any legal action or proceeding relating to these Terms shall be brought exclusively in the courts located in London, England, and you hereby consent to the jurisdiction of such courts.</p>
  
  <h2>Severability</h2>
  <p>If any provision of these Terms is held to be invalid or unenforceable, such provision shall be struck and the remaining provisions shall be enforced.</p>
  
  <h2>Entire Agreement</h2>
  <p>These Terms, together with the Privacy Policy, constitute the entire agreement between you and PrezziePal regarding the Service and supersede all prior and contemporaneous agreements, proposals, or representations, written or oral, concerning the subject matter of these Terms.</p>
  
  <h2>Contact Us</h2>
  <p>If you have any questions about these Terms, please contact us at:</p>
  <ul>
    <li>Email: hello@prezziepal.com</li>
  </ul>
`;
